import { IImportLeadPayload, PaySource } from '../types/api';
import { GaEvent, GaTransaction } from '../types/ga';
import { IProduct } from '../types/product';
import { toFixedFloat } from '../utils/numbers';

export type GaPaymentButtonType =
  | 'paypal_express'
  | 'klarna'
  | 'paypal'
  | 'credit_card'
  | 'apple_pay'
  | 'google_pay'
  | 'klarna_stripe';

interface IQuizAnswer {
  brand: string;
  step: number;
  key: string;
  answer: string | string[];
  questionType: string;
}

export class Gtm {
  private static logEvent = (event: GaEvent) => {
    if (window?.dataLayer) {
      window.dataLayer.push(event);
    }
  };

  static trackBuyButtonClick = (
    email: string,
    orderId: string,
    buttonType: GaPaymentButtonType,
  ) => {
    Gtm.logEvent({
      event: 'payment_button_click',
      user_id: email,
      transaction_id: orderId,
      payment_type: buttonType,
    });
  };

  static trackPageView = (category: string, productName: string) => {
    Gtm.logEvent({
      event: 'eec.checkoutStep',
      ecommerce: {
        checkout: {
          category,
          PageViewProduct: productName,
        },
      },
    });
  };

  static trackQuizAnswer = (answer: IQuizAnswer) => {
    Gtm.logEvent({
      event: 'quiz_answer',
      ...answer,
    });
  };

  static trackQuizStart = (brand: string) => {
    Gtm.logEvent({
      event: 'quiz_start',
      brand,
    });
  };

  static trackQuizFinish = (brand: string, email: string) => {
    Gtm.logEvent({
      event: 'quiz_finish',
      brand,
      user_id: email,
    });
  };

  static trackEmail = (email: string) => {
    Gtm.logEvent({
      event: 'Email_Event',
      user_id: email,
    });
  };

  static trackUpsellButtonClick = (
    paymentType: PaySource,
    orderId?: string,
    email?: string,
  ) => {
    Gtm.logEvent({
      event: `upsell_payment_button_click`,
      user_id: email,
      transaction_id: orderId,
      payment_type: paymentType,
    });
  };

  static trackTransaction = (
    client: string,
    lead: IImportLeadPayload,
    product: IProduct,
    upsellKey?: number,
  ) => {
    const category =
      typeof upsellKey === 'number' ? `Upsell${upsellKey + 1}` : 'Order';

    Gtm.logEvent({
      event: 'eec.transaction',
      ecommerce: {
        currencyCode: product.currencyCode,
        purchase: {
          actionField: {
            id: lead.orderId,
            tax: '',
            coupon: '',
            email: lead.emailAddress,
            shipping: product.shippingPrice,
            revenue: toFixedFloat(product.finalPrice + product.shippingPrice),
          },
          products: [
            {
              id: String(product.id),
              name: product.name,
              brand: client,
              price: String(product.finalPrice),
              quantity: product.quantity,
              category,
              variant: '',
              position: '',
            },
          ],
        } as GaTransaction,
      },
    });
  };
}
